import type {IntlShape} from 'react-intl'

/** Populated on national-exam notifications from the API / Socket.IO. */
export type NationalExamNotificationRef = {
  _id?: string
  id?: string
  title?: string
  subject?: string | {name?: string; _id?: string}
  level?: string
  type?: string
  year?: number
  session?: string
}

export function getNationalExamFromNotification(
  notification: {nationalExam?: unknown}
): NationalExamNotificationRef | null {
  const ne = notification.nationalExam
  if (!ne || typeof ne !== 'object') {
    return null
  }
  return ne as NationalExamNotificationRef
}

export function formatNationalExamNotificationSubject(
  subject: NationalExamNotificationRef['subject']
): string {
  if (!subject) {
    return '—'
  }
  if (typeof subject === 'string' && subject.trim()) {
    return subject.trim()
  }
  if (typeof subject === 'object' && subject.name?.trim()) {
    return subject.name.trim()
  }
  return '—'
}

export function formatNationalExamNotificationLevel(
  intl: IntlShape,
  level?: string
): string {
  const raw = (level ?? '').trim().toLowerCase().replace('ème', 'eme')
  if (raw === '6eme' || raw === '6') {
    return intl.formatMessage({
      id: 'NATIONAL_EXAMS.LEVEL_6',
      defaultMessage: '6th grade (basic)',
    })
  }
  if (raw === '9eme' || raw === '9') {
    return intl.formatMessage({
      id: 'NATIONAL_EXAMS.LEVEL_9',
      defaultMessage: '9th grade (basic)',
    })
  }
  return level?.trim() || '—'
}

export function formatNationalExamNotificationType(
  intl: IntlShape,
  type?: string
): string {
  const raw = (type ?? '').trim().toLowerCase()
  if (raw === 'exam') {
    return intl.formatMessage({
      id: 'NATIONAL_EXAMS.TAB_EXAMS',
      defaultMessage: 'Exams',
    })
  }
  if (raw === 'video') {
    return intl.formatMessage({
      id: 'NATIONAL_EXAMS.TAB_VIDEOS',
      defaultMessage: 'Videos',
    })
  }
  if (raw === 'qcm') {
    return intl.formatMessage({
      id: 'NATIONAL_EXAMS.TAB_QCM',
      defaultMessage: 'QCM',
    })
  }
  return type?.trim() || '—'
}

/** Short subtitle for in-app notification list (title · subject · level · type). */
export function formatNationalExamNotificationSubtitle(
  intl: IntlShape,
  ne: NationalExamNotificationRef | null
): string {
  if (!ne) {
    return ''
  }
  const title = ne.title?.trim()
  const subject = formatNationalExamNotificationSubject(ne.subject)
  const level = formatNationalExamNotificationLevel(intl, ne.level)
  const type = formatNationalExamNotificationType(intl, ne.type)
  const parts = [title, subject, level, type].filter((p) => p && p !== '—')
  return parts.join(' · ')
}

/** Browser push body (no react-intl); French defaults match legacy push copy. */
export function formatNationalExamNotificationPushBody(
  ne: NationalExamNotificationRef | null,
  notificationType?: string
): string {
  if (notificationType === 'NATIONAL_EXAM_CORRECTION_ADDED') {
    const title = ne?.title?.trim()
    return title
      ? `Correction disponible : ${title}`
      : 'Une correction a été ajoutée à un examen national.'
  }
  if (!ne) {
    return 'Une nouvelle ressource est disponible sur Examens nationaux.'
  }
  const title = ne.title?.trim()
  const subject = formatNationalExamNotificationSubject(ne.subject)
  const level = (ne.level ?? '').trim()
  const type = (ne.type ?? '').trim()
  const parts = [title, subject, level, type].filter(Boolean)
  return parts.length > 0
    ? parts.join(' · ')
    : 'Une nouvelle ressource est disponible sur Examens nationaux.'
}

/** Browser push title (French). */
export function formatNationalExamNotificationPushTitle(notificationType?: string): string {
  if (notificationType === 'NATIONAL_EXAM_CORRECTION_ADDED') {
    return 'Correction — Examens nationaux'
  }
  return 'Nouvelle ressource — Examens nationaux'
}
